import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';

interface ManualScanInputProps {
  onSubmit: (code: string) => void;
  placeholder?: string;
}

/**
 * Manual barcode entry for desktop testing.
 *
 * The scanner screens are designed for Bluetooth HID scanners on iPad.
 * This field lets developers (and desk-based staff) type or paste a
 * barcode value and submit it to the same handler the scanner uses.
 *
 * Does not interfere with useBarcodeScanner — the hook's timing filter
 * naturally ignores slow human keystrokes, and this component calls
 * onSubmit directly rather than simulating scanner input.
 */
export default function ManualScanInput({
  onSubmit,
  placeholder = 'Type or paste barcode...',
}: ManualScanInputProps) {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    const trimmed = value.trim();
    if (trimmed) {
      onSubmit(trimmed);
      setValue('');
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <TextField
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
          // Stop propagation so useBarcodeScanner doesn't see these keystrokes
          e.stopPropagation();
        }}
        placeholder={placeholder}
        size="small"
        fullWidth
        sx={{ maxWidth: 360 }}
      />
      <IconButton
        onClick={handleSubmit}
        color="primary"
        disabled={!value.trim()}
        aria-label="Submit barcode"
      >
        <SendIcon />
      </IconButton>
    </Box>
  );
}
